import React from 'react';
import PropTypes from 'prop-types';
import CreateQuoteStep1Component from '../../quotes/step1/CreateQuoteStep1Component';
import {
  CreateQuoteStep1Settings,
  CreateQuoteStep1,
  quoteCreationType,
} from '../../quotes/otherQuoteEntities';
import QuoteWizardContext from '../../quotes/QuoteWizardContext';
import { makeConcealable } from '@ami/react/core';
import { ProductConfiguration } from '../../products/productConfiguration';
import { Machine } from '../../machines/machine';
import { MachineFilter } from '../../machines/machineFilter';
import { appConfig } from '@ami/react/core';
import { makeValidable } from '@ami/react/core';
import { Spinner } from '@ami/react/core';
import { quoteWizardMode } from '../../quotes/quoteWizardMode';
import { PriceZone } from '../../price-zones/priceZone';
import { machineStatus } from '../../machines/machineStatusType';

const ValidableDataComponent = makeValidable(CreateQuoteStep1Component);

class CreateQuoteStep1Container extends React.Component {
  constructor(props) {
    super(props);
    this.updateData = this.updateData.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.validableRef = React.createRef();
  }
  static contextType = QuoteWizardContext;
  static propTypes = {
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    onContinue: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    setContact: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(
      Object.keys(quoteWizardMode).map((item) => quoteWizardMode[item])
    ).isRequired,
  };

  state = {
    contact: null,
    priceZones: [],
    machines: [],
    data: this.initializeStep1Data(),
    loading: true,
    loadingMachines: false,
    showValidations: false,
  };

  isQuickPriceLookupMode() {
    return this.props.mode === quoteWizardMode.quickPriceLookupMode;
  }

  isAmiPriceLookupMode() {
    return this.props.mode === quoteWizardMode.amiPriceLookupMode;
  }

  initializeStep1Data() {
    const data = new CreateQuoteStep1();
    if (this.isQuickPriceLookupMode() || this.isAmiPriceLookupMode()) {
      data.type = quoteCreationType.byWeightClass;
    }
    return data;
  }

  setTerritoryManager = (territoryManager) => {
    this.setState({ contact: territoryManager });
    this.props.setContact(territoryManager);
  };

  async componentDidMount() {
    const state = {
      loading: false,
    };

    if (appConfig.isManagementApp) {
      state.priceZones = await PriceZone.summarySearch();

      if (this.isAmiPriceLookupMode()) {
        this.props.setContact(null);
      } else {
        const { data } = this.state;

        const step1Settings = await CreateQuoteStep1Settings.getForAmiUser();
        if (step1Settings) {
          data.dealerGroupId = step1Settings.dealerGroupId;
          data.dealerGroupName = step1Settings.dealerGroupName;
          data.dealerBranchSummary = step1Settings.dealerBranchSummary;
          data.dealerBranchId = data.dealerBranchSummary.dealerBranchId;
          data.dealerBranchName = data.dealerBranchSummary.dealerBranchName;
          data.priceZoneSummary = state.priceZones.find(
            (x) => x.id === data.dealerBranchSummary.priceZoneId
          );
          data.priceZoneId = data.dealerBranchSummary.priceZoneId;
          this.setTerritoryManager(data.dealerBranchSummary.territoryManager);
        }

        state.data = data;
      }
    } else if (appConfig.isDealerApp) {
      const { data } = this.state;

      const step1Settings = await CreateQuoteStep1Settings.getForDealerUser();
      if (step1Settings) {
        state.notAuthorized = false;

        data.dealerGroupId = step1Settings.dealerGroupId;
        data.dealerGroupName = step1Settings.dealerGroupName;
        data.dealerBranchSummary = step1Settings.dealerBranchSummary;
        data.dealerBranchId = data.dealerBranchSummary.dealerBranchId;
        data.dealerBranchName = data.dealerBranchSummary.dealerBranchName;
        data.priceZoneId = data.dealerBranchSummary.priceZoneId;
        this.setTerritoryManager(data.dealerBranchSummary.territoryManager);
      } else {
        state.notAuthorized = true;
      }

      state.data = data;
    }

    this.setState(state);
  }

  async updateData(updateObject) {
    const oldData = this.state.data;
    const isCarrierChange = !!(
      updateObject.carrierId && oldData.carrierId !== updateObject.carrierId
    );
    const isBranchChange = !!(
      updateObject.dealerBranchId &&
      oldData.dealerBranchId !== updateObject.dealerBranchId
    );
    const isPriceZoneChange = !!(
      updateObject.priceZoneId &&
      oldData.priceZoneId !== updateObject.priceZoneId
    );

    const newState = {
      data: new CreateQuoteStep1({ ...this.state.data, ...updateObject }),
      loadingMachines: isCarrierChange,
    };

    if (isBranchChange) {
      newState.data.carrierId = null;
    }

    if (isPriceZoneChange) {
      newState.data.priceZoneSummary = this.state.priceZones.find(
        (x) => x.id === updateObject.priceZoneId
      );
    }

    this.setState(newState, async () => {
      let machines = this.state.machines;
      if (isCarrierChange) {
        machines = await Machine.search(
          new MachineFilter({
            carrierId: newState.data.carrierId,
            status: machineStatus.Approved,
          })
        );
        this.setState({ machines, loadingMachines: false });
        this.context.setQuoteCarrierMachines(machines);
      }
      if (isCarrierChange || isBranchChange) {
        this.setPreferredData(newState.data, isBranchChange, machines);
      }
    });
  }

  setPreferredData(data, isBranchChange, machines) {
    const { carrierId, dealerBranchId } = data;

    if (
      !isBranchChange &&
      carrierId &&
      this.props.productConfiguration.carriers.find((x) => x.id === carrierId)
        .weightCategories.length === 1
    ) {
      data.type = quoteCreationType.byMakeAndModel;
    }

    if (this.isQuickPriceLookupMode() || this.isAmiPriceLookupMode()) {
      // if the context is the Quick (or AMI) Price Lookup wizard, we shouldn't preselect the Make.
      // Just need to save the state to persist the new quoteCreationType and return
      this.setState({ data });
      return;
    }

    if (!dealerBranchId || !carrierId) {
      return;
    }

    // If we already have Model selected, then we should not update the Make when the dealer branch is changed.
    if (isBranchChange && data.model !== '') {
      return;
    }

    data.manufacturer = null;
    data.model = '';

    if (
      data.dealerBranchSummary.preferredMakes &&
      data.dealerBranchSummary.preferredMakes.length > 0
    ) {
      data.type = quoteCreationType.byMakeAndModel;
      const preferredMake = data.dealerBranchSummary.preferredMakes[0];
      if (machines.some((x) => x.manufacturer === preferredMake)) {
        data.manufacturer = preferredMake;
      }
    }

    this.setState({ data });
  }

  handleSubmit(skipMakeAndModel, weightCategory) {
    var canSkip =
      this.isQuickPriceLookupMode() ||
      this.isAmiPriceLookupMode() ||
      this.state.data.dealerBranchId;
    if (skipMakeAndModel && canSkip) {
      this.submit(skipMakeAndModel, weightCategory);
    } else {
      this.setState({ showValidations: true }, () => {
        if (this.validableRef.current.isValid()) {
          this.submit(skipMakeAndModel, weightCategory);
        }
      });
    }
  }

  submit(skipMakeAndModel, weightCategory) {
    const step1Data = new CreateQuoteStep1({
      ...this.state.data,
    });
    if (skipMakeAndModel) {
      step1Data.type = quoteCreationType.byWeightClass;
      step1Data.weightCategoryId = weightCategory.id;
    }
    this.props.onContinue(step1Data);
  }

  render() {
    if (this.state.loading) {
      return (
        <div className="container text-center">
          <h4 className="mt-5 mb-2">Loading</h4>
          <Spinner show={true} size="fa-5x" />
        </div>
      );
    }

    return (
      <form onSubmit={this.handleSubmit} className="mt-2">
        <ValidableDataComponent
          ref={this.validableRef}
          showValidations={this.state.showValidations}
          data={this.state.data}
          submitting={this.props.submitting}
          machines={this.state.machines}
          productConfiguration={this.props.productConfiguration}
          onUpdate={this.updateData}
          onContinue={this.handleSubmit}
          onCarrierSelected={(carrierId) => this.loadMachines(carrierId)}
          loadingMachines={this.state.loadingMachines}
          isAmiPriceLookupMode={
            this.props.mode === quoteWizardMode.amiPriceLookupMode
          }
          notAuthorized={this.state.notAuthorized}
          priceZones={this.state.priceZones}
        />
      </form>
    );
  }
}

export default makeConcealable(CreateQuoteStep1Container);
